// ─── types.ts — Shared Domain Types ─────────────────────────────────────
//
// WHY THIS FILE EXISTS:
// These interfaces (City, TripLeg, TrainRoute, StopActions, DaySegment)
// are imported by 11+ files spanning components, hooks, and services.
// Co-locating them with any single service would create false coupling.
//
// RULE OF THUMB:
//   1-2 consumers in the same layer → co-locate with the owner.
//   3+ consumers across layers      → put it here.
//
// HELPERS: trainRouteToTripLeg is exported here for use by useTripPlanner
// and useGhostLegs — cleaner than duplication.
//
// DATA: lives in cityStore.ts + Supabase. No data arrays here.
// ─────────────────────────────────────────────────────────────────────────

// ─── Types ───────────────────────────────────────────────────────────────

export interface StopActions {
  [stationId: string]: {
    food: boolean;
    overnight: boolean;
    fika: boolean;
    nights?: number;
  };
}

/** City enriched with journey-specific arrival/departure times. */
export type CityWithTimes = City & { arrival?: string; departure?: string; arrivalInstant?: string; departureInstant?: string };

export interface City {
  id: string;
  name: string;
  city: string;
  country: string;
  population: number;
  latitude: number;
  longitude: number;
  attractions?: string[];
  foodSpecialties?: string[];
  // POI-specific fields (populated when clicking a POI marker on the map)
  poiType?: string | null;
  poiName?: string | null;
  imageUrl?: string | null;
  poiDescription?: string | null;
  poiReason?: string | null;
  poiShowcaseText?: string | null;
  spotifyUri?: string | null;
  /** City/place description from Supabase */
  description?: string | null;
  /** Resolved numeric HAFAS EVA ID (avoids re-resolution via HAFAS API) */
  hafasId?: string;
  /** Transient: set when a search result was found via a local-language alias */
  matchedAlias?: string;
}

export interface TrainRoute {
  id: string;
  name: string;
  from: string;
  to: string;
  stations: string[];
  duration: string;
  isSleeper: boolean;
  operator: string;
  frequency: string;
  departureTime: string;
  arrivalTime: string;
  /** ISO date of departure (e.g. "2026-02-27"). Populated from HAFAS timestamps. */
  departureDate?: string;
  /** ISO date of arrival (e.g. "2026-02-28"). Populated from HAFAS timestamps. */
  arrivalDate?: string;
  departureInstant?: string;
  arrivalInstant?: string;
  /** ISO day numbers (1=Mon..7=Sun). Empty/undefined = daily */
  runningDays?: number[];
  /** Start of seasonal validity (ISO date string). null = year-round */
  validFrom?: string | null;
  /** End of seasonal validity (ISO date string). null = year-round */
  validUntil?: string | null;
  /** Direct booking link */
  bookingUrl?: string | null;
  /** Price information from HAFAS */
  price?: {
    amount: number;
    currency: string;
  } | null;
  /** Data provenance: 'manual' | 'gtfs' */
  source?: string;
  /** Pre-resolved polyline coordinates captured at fetch time.
   *  Avoids stationStore lookups at map render time. */
  routeCoords?: [number, number][];
  /** Full City objects along the route with arrival/departure times.
   *  Built from HAFAS stopovers. Carries all City fields for future reuse. */
  stationData?: CityWithTimes[];
}

/** Stores journey metadata for a trip leg between two consecutive stops */
export interface TripLeg {
  fromStationId: string;
  toStationId: string;
  departureTime: string;
  arrivalTime: string;
  /** ISO date of departure (e.g. "2026-02-27"). Used for overnight detection. */
  departureDate?: string;
  /** ISO date of arrival (e.g. "2026-02-28"). Used for overnight detection. */
  arrivalDate?: string;
  departureInstant?: string;
  arrivalInstant?: string;
  duration: string;
  operator: string;
  routeId: string;
  stations: string[]; // Ordered intermediate station IDs along the route
  /** Pre-resolved polyline coordinates captured at creation time.
   *  Avoids needing stationStore lookups at render time. */
  routeCoords?: [number, number][];
  /** Computed day number this leg belongs to (1-based). Set by recomputeDayAssignments(). */
  dayNumber?: number;
  /** Computed day color for map rendering. Set by recomputeDayAssignments(). */
  dayColor?: string;
  /** Price information for this leg */
  price?: {
    amount: number;
    currency: string;
  } | null;
  /** Sleeper train — used by pricingService for reservation fee estimation */
  isSleeper?: boolean;
  /** True if this leg was auto-fetched in the background ("ghost leg").
   *  Ghost legs are replaced when the user explicitly selects a route. */
  isGhostLeg?: boolean;
  /** An itinerary edit needs an explicit train choice; never auto-fill this gap. */
  requiresSelection?: boolean;
  /** True if the ghost-leg fetch found no viable route. */
  ghostError?: boolean;
  /** True if this is a placeholder ghost leg still being fetched. */
  ghostLoading?: boolean;
  /** True if the ghost-leg fetch was blocked by a rate limit (429) and is queued for retry. */
  ghostThrottled?: boolean;
  /** Inputs that produced an automatic connection; excludes its own progress. */
  ghostScheduleKey?: string;
  /** Full City objects along the route with arrival/departure times.
   *  Built from HAFAS stopovers. Carries all City fields for future reuse. */
  stationData?: CityWithTimes[];
}

/** Convert a TrainRoute (HAFAS search result) into a TripLeg.
 *  Shared by useTripPlanner (manual adds) and useGhostLegs (auto-fetched). */
export function trainRouteToTripLeg(
  route: TrainRoute,
  opts: {
    fromStationId: string;
    toStationId: string;
    routeCoords?: [number, number][];
    isGhostLeg?: boolean;
    stationData?: CityWithTimes[];
  },
): TripLeg {
  return {
    fromStationId: opts.fromStationId,
    toStationId: opts.toStationId,
    departureTime: route.departureTime,
    arrivalTime: route.arrivalTime,
    departureDate: route.departureDate,
    arrivalDate: route.arrivalDate,
    departureInstant: route.departureInstant,
    arrivalInstant: route.arrivalInstant,
    duration: route.duration,
    operator: route.operator,
    routeId: route.id,
    stations: route.stations,
    routeCoords: opts.routeCoords && opts.routeCoords.length >= 2 ? opts.routeCoords : undefined,
    price: route.price,
    isSleeper: route.isSleeper,
    isGhostLeg: opts.isGhostLeg ?? false,
    stationData: opts.stationData ?? route.stationData,
  };
}

export interface DaySegment {
  day: number;
  stationIds: string[];
  color: string;
}

// ─── Search Filters ──────────────────────────────────────────────────────

export interface SearchFilters {
  sleeperTrainsOnly: boolean;
  maxDuration: number | null; // in hours
  maxStations: number | null;
}

// ─── Explore Filters ─────────────────────────────────────────────────────

export interface ExploreFilters {
  showLandmarks: boolean;
  showHiddenGems: boolean;
  showCastles: boolean;
  showNaturalLandmarks: boolean;
}

export const DEFAULT_EXPLORE_FILTERS: ExploreFilters = {
  showLandmarks: true,
  showHiddenGems: true,
  showCastles: true,
  showNaturalLandmarks: true,
};

export type ViewMode = "search" | "explore";

